import { z } from "zod";
import { timestampSchema } from "./common.js";

export enum TestAssetType {
  MESSAGE_TEMPLATE = "MESSAGE_TEMPLATE",
  PARAMETER_SET = "PARAMETER_SET",
  DATASET = "DATASET",
  SIMULATION_PROFILE = "SIMULATION_PROFILE",
  TEST_CASE = "TEST_CASE",
  TEST_SUITE = "TEST_SUITE",
  CONNECTION_PROFILE = "CONNECTION_PROFILE",
  REPORT_TEMPLATE = "REPORT_TEMPLATE",
  SIMULATION_TEST = "SIMULATION_TEST",
}

export const testAssetTypeSchema = z.nativeEnum(TestAssetType);

export enum AssetStatus {
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
}

export const assetStatusSchema = z.nativeEnum(AssetStatus);

export enum ParameterScope {
  GLOBAL = "GLOBAL",
  MESSAGE_TYPE = "MESSAGE_TYPE",
  TEMPLATE = "TEMPLATE",
}

export const parameterScopeSchema = z.nativeEnum(ParameterScope);

export enum DatasetFormat {
  CSV = "CSV",
  JSON = "JSON",
  JSONL = "JSONL",
}

export const datasetFormatSchema = z.nativeEnum(DatasetFormat);

export enum DatasetStorageMode {
  INLINE = "INLINE",
  FIRESTORE = "FIRESTORE",
  FILE = "FILE",
}

export const datasetStorageModeSchema = z.nativeEnum(DatasetStorageMode);

export enum DatasetRowStrategy {
  FIRST = "FIRST",
  SEQUENTIAL = "SEQUENTIAL",
  RANDOM = "RANDOM",
  LOOP = "LOOP",
}

export const datasetRowStrategySchema = z.nativeEnum(DatasetRowStrategy);

export interface TestAsset {
  [key: string]: unknown;
  id: string;
  assetType: TestAssetType;
  projectId: string;
  name: string;
  description: string;
  version: string;
  revision: number;
  parentId?: string;
  tags: string[];
  status: AssetStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  metadata: Record<string, unknown>;
}

export const testAssetSchema: z.ZodSchema<TestAsset> = z
  .object({
    id: z.string(),
    assetType: testAssetTypeSchema,
    projectId: z.string(),
    name: z.string(),
    description: z.string(),
    version: z.string(),
    revision: z.number().int(),
    parentId: z.string().optional(),
    tags: z.array(z.string()),
    status: assetStatusSchema,
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastUsedAt: timestampSchema.optional(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

export interface MessageTemplate {
  [key: string]: unknown;
  id: string;
  projectId: string;
  assetType: TestAssetType.MESSAGE_TEMPLATE;
  name: string;
  description: string;
  messageDefinitionId?: string;
  messageType: number;
  baseTemplateId?: string;
  payload: Record<string, unknown>;
  parameterSetIds: string[];
  datasetBindings: DatasetBinding[];
  tags: string[];
  version: string;
  revision: number;
  parentId?: string;
  status: AssetStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  metadata: Record<string, unknown>;
}

export const messageTemplateSchema: z.ZodSchema<MessageTemplate> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    assetType: z.literal(TestAssetType.MESSAGE_TEMPLATE),
    name: z.string(),
    description: z.string(),
    messageDefinitionId: z.string().optional(),
    messageType: z.number().int(),
    baseTemplateId: z.string().optional(),
    payload: z.record(z.unknown()),
    parameterSetIds: z.array(z.string()),
    datasetBindings: z.array(z.any()),
    tags: z.array(z.string()),
    version: z.string(),
    revision: z.number().int(),
    parentId: z.string().optional(),
    status: assetStatusSchema,
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastUsedAt: timestampSchema.optional(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

export interface ParameterSet {
  [key: string]: unknown;
  id: string;
  projectId: string;
  assetType: TestAssetType.PARAMETER_SET;
  name: string;
  description: string;
  messageType?: number;
  scope: ParameterScope;
  values: Record<string, unknown>;
  tags: string[];
  version: string;
  revision: number;
  parentId?: string;
  status: AssetStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  metadata: Record<string, unknown>;
}

export const parameterSetSchema: z.ZodSchema<ParameterSet> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    assetType: z.literal(TestAssetType.PARAMETER_SET),
    name: z.string(),
    description: z.string(),
    messageType: z.number().int().optional(),
    scope: parameterScopeSchema,
    values: z.record(z.unknown()),
    tags: z.array(z.string()),
    version: z.string(),
    revision: z.number().int(),
    parentId: z.string().optional(),
    status: assetStatusSchema,
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastUsedAt: timestampSchema.optional(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

export interface DatasetBinding {
  datasetId: string;
  column: string;
  fieldKey: string;
  transform?: string;
  defaultValue?: unknown;
  required?: boolean;
  strategy?: DatasetRowStrategy;
}

export const datasetBindingSchema = z.object({
  datasetId: z.string(),
  column: z.string(),
  fieldKey: z.string(),
  transform: z.string().optional(),
  defaultValue: z.unknown().optional(),
  required: z.boolean().optional(),
  strategy: datasetRowStrategySchema.optional(),
});

export interface DatasetRow {
  [column: string]: unknown;
}

export interface Dataset {
  [key: string]: unknown;
  id: string;
  projectId: string;
  assetType: TestAssetType.DATASET;
  name: string;
  description: string;
  format: DatasetFormat;
  columns: string[];
  rowCount: number;
  storageMode: DatasetStorageMode;
  previewRows: DatasetRow[];
  rows?: DatasetRow[];
  bindings: DatasetBinding[];
  tags: string[];
  version: string;
  revision: number;
  parentId?: string;
  status: AssetStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt?: string;
  metadata: Record<string, unknown>;
}

export const datasetSchema: z.ZodSchema<Dataset> = z
  .object({
    id: z.string(),
    projectId: z.string(),
    assetType: z.literal(TestAssetType.DATASET),
    name: z.string(),
    description: z.string(),
    format: datasetFormatSchema,
    columns: z.array(z.string()),
    rowCount: z.number().int(),
    storageMode: datasetStorageModeSchema,
    previewRows: z.array(z.record(z.unknown())),
    rows: z.array(z.record(z.unknown())).optional(),
    bindings: z.array(datasetBindingSchema),
    tags: z.array(z.string()),
    version: z.string(),
    revision: z.number().int(),
    parentId: z.string().optional(),
    status: assetStatusSchema,
    createdBy: z.string(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    lastUsedAt: timestampSchema.optional(),
    metadata: z.record(z.unknown()),
  })
  .passthrough();

export interface ResolvedMessageTemplate {
  templateId: string;
  messageType: number;
  messageDefinitionId?: string;
  resolvedPayload: Record<string, unknown>;
  appliedSources: Array<{
    source: string;
    id: string;
    field?: string;
    value?: unknown;
  }>;
  validationResult?: { severity: string; issues: string[] };
}

export interface RuntimeMessageContext {
  sequence: number;
  timestamp: string;
  runId?: string;
  sessionId?: string;
  vehicleId?: string;
  randomSeed?: string;
  datasetRow?: DatasetRow;
}

export const resolvedMessageTemplateSchema = z.object({
  templateId: z.string(),
  messageType: z.number().int(),
  messageDefinitionId: z.string().optional(),
  resolvedPayload: z.record(z.unknown()),
  appliedSources: z.array(
    z.object({
      source: z.string(),
      id: z.string(),
      field: z.string().optional(),
      value: z.unknown().optional(),
    }),
  ),
  validationResult: z
    .object({
      severity: z.string(),
      issues: z.array(z.string()),
    })
    .optional(),
});

export const runtimeMessageContextSchema = z.object({
  sequence: z.number().int(),
  timestamp: z.string().datetime(),
  runId: z.string().optional(),
  sessionId: z.string().optional(),
  vehicleId: z.string().optional(),
  randomSeed: z.string().optional(),
  datasetRow: z.record(z.unknown()).optional(),
});
